import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ZoomIn, ZoomOut, Move, Check, X, RotateCcw, Maximize } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const ImageEditor = ({ isOpen, imageUrl, initialAdjustment, onSave, onClose, title = "Ajustar Imagem", confirmText = "Aplicar" }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [minScale, setMinScale] = useState(1);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Calculate minimum scale to fill the square container
  const calculateMinScale = useCallback((imgWidth, imgHeight, contWidth, contHeight) => {
    if (!imgWidth || !imgHeight || !contWidth || !contHeight) return 1;
    
    const widthRatio = contWidth / imgWidth;
    const heightRatio = contHeight / imgHeight;
    
    // Use the larger ratio to ensure the image fills the container
    return Math.max(widthRatio, heightRatio);
  }, []);

  // Handle image load to get natural dimensions
  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    
    setImageSize({ width: naturalWidth, height: naturalHeight });
    
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
      
      const newMinScale = calculateMinScale(naturalWidth, naturalHeight, rect.width, rect.height);
      setMinScale(newMinScale);
      
      // If no initial adjustment, set scale to minScale (fill container)
      if (!initialAdjustment) {
        setScale(newMinScale);
      }
    }
  }, [calculateMinScale, initialAdjustment]);

  // Reset state when opening with new image/adjustment
  useEffect(() => {
    if (isOpen) {
      if (initialAdjustment) {
        setScale(initialAdjustment.scale || minScale);
        setPosition({
          x: initialAdjustment.position_x || initialAdjustment.position?.x || 0,
          y: initialAdjustment.position_y || initialAdjustment.position?.y || 0
        });
      } else {
        setScale(minScale);
        setPosition({ x: 0, y: 0 });
      }
    }
  }, [isOpen, initialAdjustment, minScale]);

  // Update container size on resize
  useEffect(() => {
    if (isOpen && containerRef.current && imageSize.width > 0) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
      
      const newMinScale = calculateMinScale(imageSize.width, imageSize.height, rect.width, rect.height);
      setMinScale(newMinScale);
    }
  }, [isOpen, imageSize, calculateMinScale]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Calculate max movement based on image overflow
    const scaledWidth = imageSize.width * scale;
    const scaledHeight = imageSize.height * scale;
    const overflowX = Math.max(0, (scaledWidth - containerSize.width) / 2);
    const overflowY = Math.max(0, (scaledHeight - containerSize.height) / 2);
    
    setPosition({
      x: Math.max(-overflowX, Math.min(overflowX, newX)),
      y: Math.max(-overflowY, Math.min(overflowY, newY))
    });
  }, [isDragging, dragStart, scale, imageSize, containerSize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  }, [position]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    const scaledWidth = imageSize.width * scale;
    const scaledHeight = imageSize.height * scale;
    const overflowX = Math.max(0, (scaledWidth - containerSize.width) / 2);
    const overflowY = Math.max(0, (scaledHeight - containerSize.height) / 2);
    
    setPosition({
      x: Math.max(-overflowX, Math.min(overflowX, newX)),
      y: Math.max(-overflowY, Math.min(overflowY, newY))
    });
  }, [isDragging, dragStart, scale, imageSize, containerSize]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 3));
  };

  const zoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.1, minScale);
      // Reset position if zooming to min
      if (newScale <= minScale) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  };

  const reset = () => {
    setScale(minScale);
    setPosition({ x: 0, y: 0 });
  };

  const fitToFill = () => {
    setScale(minScale);
    setPosition({ x: 0, y: 0 });
  };

  const handleSave = () => {
    // Create a canvas to crop the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;
    
    if (!img || !containerSize.width || !containerSize.height) {
      // Fallback to just saving adjustment params
      onSave({
        scale,
        position_x: position.x,
        position_y: position.y,
        position,
        transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`
      });
      return;
    }
    
    // Set canvas to square dimensions (use container size for quality)
    const outputSize = 800; // Output size for good quality
    canvas.width = outputSize;
    canvas.height = outputSize;
    
    // Calculate what portion of the image is visible in the container
    const scaledImgWidth = imageSize.width * scale;
    const scaledImgHeight = imageSize.height * scale;
    
    // The visible area center in scaled image coordinates
    const centerX = (scaledImgWidth / 2) - position.x;
    const centerY = (scaledImgHeight / 2) - position.y;
    
    // Convert back to original image coordinates
    const srcCenterX = centerX / scale;
    const srcCenterY = centerY / scale;
    
    // The visible area size in original image coordinates
    const visibleWidth = containerSize.width / scale;
    const visibleHeight = containerSize.height / scale;
    
    // Source rectangle (in original image coordinates)
    const srcX = srcCenterX - (visibleWidth / 2);
    const srcY = srcCenterY - (visibleHeight / 2);
    const srcW = visibleWidth;
    const srcH = visibleHeight;
    
    // Draw the cropped image to canvas
    ctx.drawImage(
      img,
      srcX, srcY, srcW, srcH,  // Source rectangle
      0, 0, outputSize, outputSize  // Destination rectangle
    );
    
    // Convert to data URL
    const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.9);
    
    // Return both the cropped image and the adjustment params
    onSave({
      scale,
      position_x: position.x,
      position_y: position.y,
      position,
      croppedImage: croppedImageUrl,
      transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`
    });
  };

  const handleSliderChange = (e) => {
    const newScale = Number(e.target.value) / 100;
    if (newScale < scale) {
      // When zooming out, gradually center the position
      const ratio = newScale / scale;
      setPosition(prev => ({
        x: prev.x * ratio,
        y: prev.y * ratio
      }));
    }
    setScale(Math.max(newScale, minScale));
  };

  if (!isOpen) return null;

  // Calculate display values
  const sliderMin = Math.round(minScale * 100);
  const sliderMax = 300;
  const sliderValue = Math.round(scale * 100);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview Area - Square container */}
          <div 
            ref={containerRef}
            className="relative aspect-square bg-slate-800 rounded-xl overflow-hidden cursor-move"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Grid overlay to show cropping area */}
            <div className="absolute inset-0 pointer-events-none z-10">
              <div className="absolute inset-0 border-2 border-white/30"></div>
              <div className="absolute top-1/3 left-0 right-0 border-t border-white/20"></div>
              <div className="absolute top-2/3 left-0 right-0 border-t border-white/20"></div>
              <div className="absolute left-1/3 top-0 bottom-0 border-l border-white/20"></div>
              <div className="absolute left-2/3 top-0 bottom-0 border-l border-white/20"></div>
            </div>

            <img
              ref={imageRef}
              src={imageUrl}
              alt="Preview"
              className="absolute select-none"
              onLoad={handleImageLoad}
              style={{
                width: imageSize.width > 0 ? imageSize.width : 'auto',
                height: imageSize.height > 0 ? imageSize.height : 'auto',
                maxWidth: 'none',
                maxHeight: 'none',
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                transformOrigin: 'center center'
              }}
              draggable={false}
            />
            
            {/* Drag hint */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 z-20">
              <Move className="w-3 h-3" />
              Arraste para posicionar
            </div>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={zoomOut}
              disabled={scale <= minScale}
              className="w-10 h-10 p-0"
              title="Diminuir zoom"
            >
              <ZoomOut className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2 min-w-[80px] justify-center">
              <span className="text-sm font-medium text-slate-700">
                {sliderValue}%
              </span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={zoomIn}
              disabled={scale >= 3}
              className="w-10 h-10 p-0"
              title="Aumentar zoom"
            >
              <ZoomIn className="w-5 h-5" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={reset}
              className="w-10 h-10 p-0"
              title="Resetar"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={fitToFill}
              className="w-10 h-10 p-0"
              title="Preencher quadro"
            >
              <Maximize className="w-4 h-4" />
            </Button>
          </div>

          {/* Zoom Slider */}
          <input
            type="range"
            min={sliderMin}
            max={sliderMax}
            value={sliderValue}
            onChange={handleSliderChange}
            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />

          <p className="text-xs text-slate-500 text-center">
            Ajuste o zoom e arraste a imagem para enquadrar no formato quadrado
          </p>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-1" />
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700 text-white">
              <Check className="w-4 h-4 mr-1" />
              {confirmText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageEditor;
